import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, fmtINR } from "@/lib/api";
import { Warning, CheckCircle, Clock, Calendar, Storefront, Receipt, ArrowRight } from "@phosphor-icons/react";

const STATUS_META = {
  overdue:     { label: "Overdue",     pill: "var-pill var-up-strong",   row: "row-tint-up",   icon: <Warning size={11} weight="fill" /> },
  upcoming:    { label: "Upcoming",    pill: "var-pill var-neutral",     row: "",              icon: <Clock size={11} weight="bold" /> },
  paid:        { label: "Paid",        pill: "var-pill var-down-strong", row: "row-tint-down", icon: <CheckCircle size={11} weight="fill" /> },
  no_schedule: { label: "Ad-hoc",      pill: "var-pill var-neutral",     row: "",              icon: <Clock size={11} weight="bold" /> },
};

const FILTERS = [
  { id: "overdue",  label: "Overdue" },
  { id: "upcoming", label: "Upcoming" },
  { id: "paid",     label: "Paid" },
  { id: "all",      label: "All" },
];

export default function PaymentTasksPanel({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("overdue");
  const [groupBy, setGroupBy] = useState("category");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/payment-tasks");
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const items = useMemo(() => {
    if (!data?.items) return [];
    return filter === "all" ? data.items : data.items.filter((i) => i.status === filter);
  }, [data, filter]);

  const groups = useMemo(() => {
    const out = {};
    for (const it of items) {
      const key = groupBy === "category" ? (it.category || "other") : (it.location || "—");
      (out[key] = out[key] || []).push(it);
    }
    return out;
  }, [items, groupBy]);

  const summary = data?.summary || {};
  const today = data?.today;

  return (
    <div data-testid="payment-tasks-panel" className="space-y-4 mb-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-sm grid place-items-center bg-cyan-900/40 text-cyan-200">
              <Calendar size={15} weight="duotone" />
            </div>
            <h2 className="font-heading font-bold text-2xl tracking-tight">Payment task tracker</h2>
          </div>
          <div className="text-[11px] text-slate-400 mt-1.5">
            Reconciles your recurring task sheet against HDFC bank debits. {today && <>Today: <strong>{today}</strong></>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Pill icon={<Warning size={11} weight="fill" />} label="OVERDUE" value={summary.overdue || 0} cls="var-pill var-up-strong" />
          <Pill icon={<Clock size={11} weight="bold" />}   label="UPCOMING" value={summary.upcoming || 0} cls="var-pill var-neutral" />
          <Pill icon={<CheckCircle size={11} weight="fill" />} label="PAID"  value={summary.paid || 0} cls="var-pill var-down-strong" />
          {summary.overdue_amount > 0 && (
            <Pill icon={<Receipt size={11} weight="bold" />} label="OVERDUE ₹" value={fmtINR(summary.overdue_amount)} cls="var-pill var-up" />
          )}
        </div>
      </div>

      <div className="bg-white rounded-md border border-slate-200 p-3 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`pt-filter-${f.id}`}
                className={`text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-sm border ${
                  filter === f.id
                    ? "bg-cyan-600 text-cyan-50 border-cyan-500"
                    : "bg-slate-100 text-slate-300 border-slate-200 hover:text-cyan-200"
                }`}
              >
                {f.label} {summary[f.id] != null && <span className="ml-1 font-mono opacity-80">{summary[f.id]}</span>}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold">
            <span className="text-slate-400">Group by:</span>
            {["category", "location"].map((g) => (
              <button
                key={g}
                onClick={() => setGroupBy(g)}
                className={`px-2 py-1 rounded-sm border ${
                  groupBy === g ? "bg-cyan-600 text-cyan-50 border-cyan-500" : "bg-slate-100 text-slate-300 border-slate-200"
                }`}
                data-testid={`pt-group-${g}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center text-sm text-slate-400 py-8">Loading payment tasks…</div>
        ) : !data || data.count === 0 ? (
          <div className="text-center text-sm text-slate-300 py-10 border border-dashed border-slate-200 rounded-sm bg-slate-50">
            No payment tasks loaded. Upload <strong>TASK SHEET - ACCOUNTS.xlsx</strong> via{" "}
            <strong>Uploads → Recurring tasks</strong>.
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-slate-300 py-10">No {filter} tasks. 🎉</div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groups).map(([key, rows]) => (
              <div key={key} data-testid={`pt-group-${key}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-200 mb-2 flex items-center gap-2">
                  <span className="opacity-70">{groupBy}</span>
                  <span className="font-heading font-bold text-sm tracking-tight text-slate-100 capitalize">
                    {key.replaceAll("_", " ")}
                  </span>
                  <span className="text-slate-400 font-normal">· {rows.length}</span>
                </div>
                <div className="space-y-1.5">
                  {rows.map((it, idx) => (
                    <TaskRow key={`${it.task}-${it.vendor}-${it.location}-${it.due_date}-${idx}`} it={it} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskRow({ it }) {
  const meta = STATUS_META[it.status] || STATUS_META.no_schedule;
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded-sm border border-slate-200 ${meta.row}`}
      data-testid={`pt-row-${it.task}-${it.location || ""}`}
    >
      <span className={`${meta.pill} text-[10px] uppercase tracking-wider shrink-0`}>
        {meta.icon}
        {meta.label}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-semibold text-slate-100 text-sm">{it.task}</span>
          {it.vendor && (
            <span className="text-[12px] text-slate-300">· {it.vendor}</span>
          )}
          {it.location && (
            <span className="text-[10px] uppercase tracking-wider text-cyan-300">· {it.location}</span>
          )}
        </div>
        {it.match && (
          <div className="text-[11px] text-emerald-300 mt-0.5 flex items-center gap-1.5 flex-wrap">
            <ArrowRight size={11} weight="bold" />
            <span className="font-mono">{it.match.txn_date}</span>
            <span className="font-mono font-bold">₹{Math.round(it.match.txn_amount).toLocaleString("en-IN")}</span>
            <span className="text-[10px] text-slate-400 truncate max-w-[28rem]">{(it.match.txn_description || "").slice(0, 80)}</span>
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-500/40 bg-emerald-950/30 text-emerald-200">
              {it.match.matched_by}
            </span>
          </div>
        )}
        {!it.match && it.status === "overdue" && (
          <div className="text-[11px] text-rose-300 mt-0.5">
            No matching bank debit found within ±5 days of due date.
          </div>
        )}
      </div>

      <div className="text-right shrink-0">
        {it.due_date ? (
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Due {it.due_date}</div>
        ) : (
          <div className="text-[10px] uppercase tracking-wider text-slate-400">{it.due_raw || "—"}</div>
        )}
        {it.amount != null && (
          <div className="font-mono tabular-nums font-bold text-slate-100">{fmtINR(it.amount)}</div>
        )}
        {it.amount == null && it.amount_label && (
          <div className="text-[11px] text-slate-300">{it.amount_label}</div>
        )}
      </div>
    </div>
  );
}

function Pill({ icon, label, value, cls }) {
  return (
    <span className={`${cls} text-[10px] uppercase tracking-wider`}>
      {icon}
      <span className="opacity-80">{label}</span>
      <span className="font-bold">{value}</span>
    </span>
  );
}
